/**
 * The consultant's board card: v2 of dashboard_widget (B6, 2026-10-19; the server's copilot_board.go). v1 is one trend
 * (copilot-widget.ts, unchanged); v2 holds 1 to 12 widgets the person approves at once: on a new board made for them, or
 * below the widgets of a board the org has (read as the platform holds it, then saved whole: no other widget is
 * replaced). A widget binds a machine's ports (its alias the machine's entity id; the board reads each port's one
 * primary stream tag through the machine's roles, lib/dashboard/port-binding) or the org's tags by id (bound by the name
 * the org's tag list gives each, refusing a name two tags share, as the trend does). A pareto binds nothing: the plant's
 * scrap by defect. Pure, with the calls injected (BoardCalls, as the trend's): lib/api/copilot-imports runs them, and the
 * machine page's suggested board (components/twin/equipment/MachineBoardCard) applies the same plan.
 */
import type { DashboardConfig, DataSource, WidgetInstance, WidgetType } from "@/lib/dashboard/types";
import { readWidgetWindow, windowDates, type WidgetWindow } from "@/lib/dashboard/use-live-tag-data";
import {
  DASHBOARDS_URL,
  LIVE_TAGS,
  TREND_RANGES,
  WidgetRefused,
  addToBoard,
  dashboardUrl,
  nextRow,
  trendTagNames,
  type BoardCalls,
  type OrgTag,
  type TrendRange,
} from "./copilot-widget";

/** The kinds a board card's widget takes. */
export const BOARD_KINDS = ["kpi", "timeseries", "status-grid", "histogram", "table", "spc", "pareto"] as const;
export type BoardKind = (typeof BOARD_KINDS)[number];

/** Each kind's slots and the keys each takes (the server's copilotBoardSlots); min 0 is an optional slot. */
export const BOARD_SLOTS: Record<BoardKind, Record<string, { min: number; max: number }>> = {
  kpi: { value: { min: 1, max: 1 }, trend: { min: 0, max: 1 } },
  timeseries: { series: { min: 1, max: 4 } },
  "status-grid": { state: { min: 1, max: 12 } },
  histogram: { value: { min: 1, max: 1 } },
  table: { columns: { min: 1, max: 12 } },
  spc: { value: { min: 1, max: 1 } },
  pareto: {},
};

/** The board's widget each kind adds: a pareto is the plant's scrap by defect (the Defect Pareto widget). */
export const BOARD_WIDGET_TYPE: Record<BoardKind, WidgetType> = {
  kpi: "kpi",
  timeseries: "timeseries",
  "status-grid": "status-grid",
  histogram: "histogram",
  table: "table",
  spc: "spc",
  pareto: "defect-pareto",
};

/** Each widget's size, as the Dashboards page adds it (the widget registry's defaultSize, held to it by a test). */
export const BOARD_WIDGET_SIZE: Record<BoardKind, { w: number; h: number }> = {
  kpi: { w: 3, h: 3 },
  timeseries: { w: 6, h: 4 },
  "status-grid": { w: 4, h: 3 },
  histogram: { w: 6, h: 4 },
  table: { w: 6, h: 5 },
  spc: { w: 6, h: 4 },
  pareto: { w: 6, h: 4 },
};

/** What a widget binds: a machine's ports (by its entity id), or the org's tags (by id). */
export type BoardSource =
  | { kind: "port"; entityId: string; slots: Record<string, string[]> }
  | { kind: "tag"; slots: Record<string, string[]> };

/** The band a time series or a histogram draws: the machine's yardstick, or ends the card gives. */
export type BoardBand = { yardstick: true } | { lo?: number; hi?: number; unit?: string; label?: string };

/** One widget of a board card, as the page adds it. */
export interface BoardWidgetPlan {
  kind: BoardKind;
  title: string;
  source: BoardSource | null;
  range?: TrendRange;
  window?: WidgetWindow;
  band?: BoardBand;
}

/** How the page applies a board card (a copilot-proposals ApplyPlan, as WidgetPlan is for a trend). */
export interface BoardPlan {
  kind: "dashboard_widget";
  board: { id: string } | { name: string };
  widgets: BoardWidgetPlan[];
  url: string;
}

/** A dashboard_widget plan is a board (v2) when it holds widgets; a trend (v1) holds one widget. */
export function isBoardPlan(plan: object): plan is BoardPlan {
  return "widgets" in plan;
}

/** A dashboard_widget spec is a board (v2) when it holds widgets. */
export function isBoardSpec(spec: unknown): boolean {
  return typeof spec === "object" && spec !== null && "widgets" in spec;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function finite(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

type Checked<T> = { ok: true; value: T } | { ok: false; reason: string };

/** A widget's source as the card holds it, or why the page cannot bind it. Pure. */
function boardSourceOf(kind: BoardKind, raw: unknown, n: number): Checked<BoardSource | null> {
  const slots = BOARD_SLOTS[kind];
  const names = Object.keys(slots);
  if (names.length === 0) {
    return raw === undefined || raw === null
      ? { ok: true, value: null }
      : { ok: false, reason: `Widget ${n} is the plant's scrap by defect, which binds nothing.` };
  }
  if (!isRecord(raw)) return { ok: false, reason: `Widget ${n} binds nothing.` };
  const given = isRecord(raw.slots) ? raw.slots : {};
  if (Object.keys(given).some((name) => !(name in slots))) {
    return { ok: false, reason: `Widget ${n} binds a slot it does not have.` };
  }
  const out: Record<string, string[]> = {};
  for (const name of names) {
    const v = given[name];
    const keys = Array.isArray(v) ? v.map(text) : [];
    const { min, max } = slots[name];
    if (keys.length < min || keys.length > max || keys.some((k) => !k) || new Set(keys).size < keys.length) {
      return { ok: false, reason: `Widget ${n} does not bind its ${name} as the board takes it.` };
    }
    if (keys.length > 0) out[name] = keys;
  }
  if (raw.kind === "port") {
    const entityId = text(raw.alias);
    if (!entityId) return { ok: false, reason: `Widget ${n} names no machine.` };
    return { ok: true, value: { kind: "port", entityId, slots: out } };
  }
  if (raw.kind === "tag") {
    for (const name of Object.keys(out)) {
      out[name] = out[name].map((k) => k.toLowerCase());
      if (!out[name].every((k) => UUID_RE.test(k))) return { ok: false, reason: `Widget ${n} names a tag that is not a tag id.` };
    }
    return { ok: true, value: { kind: "tag", slots: out } };
  }
  return { ok: false, reason: `Widget ${n} binds neither a machine's ports nor the org's tags.` };
}

/** A widget's band as the card holds it, or why not. Pure. */
function boardBandOf(kind: BoardKind, raw: unknown, source: BoardSource | null, n: number): Checked<BoardBand | undefined> {
  if (raw === undefined || raw === null) return { ok: true, value: undefined };
  if (kind !== "timeseries" && kind !== "histogram") return { ok: false, reason: `Widget ${n} draws no band.` };
  if (!isRecord(raw)) return { ok: false, reason: `Widget ${n}'s band does not read.` };
  if (raw.yardstick === true) {
    return source?.kind === "port"
      ? { ok: true, value: { yardstick: true } }
      : { ok: false, reason: `Widget ${n} draws a machine's yardstick but binds no machine.` };
  }
  const lo = finite(raw.lo);
  const hi = finite(raw.hi);
  if (lo === undefined && hi === undefined) return { ok: false, reason: `Widget ${n}'s band has no end.` };
  if (lo !== undefined && hi !== undefined && !(lo < hi)) {
    return { ok: false, reason: `Widget ${n}'s band's low end is above its high end.` };
  }
  const unit = text(raw.unit);
  const label = text(raw.label);
  return {
    ok: true,
    value: {
      ...(lo !== undefined ? { lo } : {}),
      ...(hi !== undefined ? { hi } : {}),
      ...(unit ? { unit } : {}),
      ...(label ? { label } : {}),
    },
  };
}

/** One widget of a board card as the page adds it, or why not, checked as the server checked it. Pure. */
function boardWidgetOf(raw: unknown, n: number): Checked<BoardWidgetPlan> {
  if (!isRecord(raw)) return { ok: false, reason: `Widget ${n} does not read.` };
  if (typeof raw.kind !== "string" || !(BOARD_KINDS as readonly string[]).includes(raw.kind)) {
    return { ok: false, reason: `Widget ${n} is a kind the board does not take.` };
  }
  const kind = raw.kind as BoardKind;
  const title = text(raw.title);
  if (!title || [...title].length > 80) return { ok: false, reason: `Widget ${n} has no title of at most 80 characters.` };
  const source = boardSourceOf(kind, raw.source, n);
  if (!source.ok) return source;
  const band = boardBandOf(kind, raw.band, source.value, n);
  if (!band.ok) return band;
  const out: BoardWidgetPlan = { kind, title, source: source.value };
  if (raw.range !== undefined && raw.range !== null && raw.range !== "") {
    if (typeof raw.range !== "string" || !(TREND_RANGES as readonly string[]).includes(raw.range)) {
      return { ok: false, reason: `Widget ${n} has a time range the board does not take.` };
    }
    out.range = raw.range as TrendRange;
  }
  if (raw.window !== undefined && raw.window !== null) {
    const win = readWidgetWindow({ window: raw.window });
    if (!win) return { ok: false, reason: `Widget ${n}'s window is not a run of at most 400 plant days.` };
    if (out.range) return { ok: false, reason: `Widget ${n} pins a range and a window.` };
    out.window = win;
  }
  if (band.value) out.band = band.value;
  return { ok: true, value: out };
}

/**
 * A board card's spec as the page applies it, or why not: the board (one the org has by id, which decides when both are
 * there, or a new one by name) and 1 to 12 widgets. The server checked it before storing it, so a refusal here is rare.
 * Pure.
 */
export function boardPlanOf(spec: Record<string, unknown>): { ok: true; plan: BoardPlan } | { ok: false; reason: string } {
  const raw = Array.isArray(spec.widgets) ? spec.widgets : [];
  if (raw.length < 1 || raw.length > 12) return { ok: false, reason: "The proposed board needs 1 to 12 widgets." };
  const widgets: BoardWidgetPlan[] = [];
  for (const [i, w] of raw.entries()) {
    const got = boardWidgetOf(w, i + 1);
    if (!got.ok) return got;
    widgets.push(got.value);
  }
  if (spec.dashboard_id === null || spec.dashboard_id === undefined) {
    const name = text(spec.dashboard_name);
    if (!name) return { ok: false, reason: "The proposed board has no name." };
    if ([...name].length > 80) return { ok: false, reason: "The proposed board's name is too long." };
    return { ok: true, plan: { kind: "dashboard_widget", board: { name }, widgets, url: DASHBOARDS_URL } };
  }
  const id = text(spec.dashboard_id).toLowerCase();
  if (!UUID_RE.test(id)) return { ok: false, reason: "The proposed board names no dashboard." };
  return { ok: true, plan: { kind: "dashboard_widget", board: { id }, widgets, url: dashboardUrl(id) } };
}

/** Every tag id a board plan's tag sources bind, once each, in the plan's order. */
function boardTagIds(plan: { widgets: readonly BoardWidgetPlan[] }): string[] {
  const ids = new Set<string>();
  for (const w of plan.widgets) {
    if (w.source?.kind === "tag") for (const keys of Object.values(w.source.slots)) for (const k of keys) ids.add(k);
  }
  return [...ids];
}

/** The names each tag source binds its tags by, or why one cannot be bound (trendTagNames over every tag at once). */
export function boardTagNames(
  plan: { widgets: readonly BoardWidgetPlan[] },
  tags: readonly OrgTag[],
): Map<string, string> | { refusal: string } {
  const ids = boardTagIds(plan);
  const bound = trendTagNames(ids, tags);
  if ("refusal" in bound) return bound;
  return new Map(ids.map((id, i) => [id, bound.names[i]]));
}

/** A widget's data source as the board holds it: a machine's ports by its id, or the org's live tags by name. */
function boardDataSource(source: BoardSource, names: ReadonlyMap<string, string>): DataSource {
  const slots: Record<string, string[]> = {};
  if (source.kind === "port") {
    for (const [slot, ports] of Object.entries(source.slots)) slots[slot] = [...ports];
    return { kind: "port", alias: source.entityId, byEntity: true, slots };
  }
  for (const [slot, ids] of Object.entries(source.slots)) slots[slot] = ids.map((id) => names.get(id) ?? id);
  return { kind: "tag", alias: LIVE_TAGS, slots };
}

/** What a widget pinned to a window of days says it shows: "Data from 1 Mar 2026 to 30 Apr 2026". Pure. */
export function windowSubtitle(w: WidgetWindow): string {
  return `Data from ${windowDates(w)}`;
}

/** A widget's config: its pinned window (which wins on the board) or range, and its band. */
function boardConfig(w: BoardWidgetPlan): Record<string, unknown> | undefined {
  const config: Record<string, unknown> = {};
  if (w.window) config.window = { ...w.window };
  else if (w.range) config.range = w.range;
  if (w.band) config.band = { ...w.band };
  return Object.keys(config).length > 0 ? config : undefined;
}

/**
 * The plan's widgets as the board holds them, packed left to right in rows of 12 columns from `row`, each at the size
 * the Dashboards page gives it, with ids the page gives (`newId`). `names` binds each tag source's ids by name. Pure.
 */
export function boardWidgetsOf(
  plan: { widgets: readonly BoardWidgetPlan[] },
  names: ReadonlyMap<string, string>,
  newId: () => string,
  row = 0,
): WidgetInstance[] {
  const out: WidgetInstance[] = [];
  let x = 0;
  let y = row;
  let tallest = 0;
  for (const w of plan.widgets) {
    const size = BOARD_WIDGET_SIZE[w.kind];
    if (x + size.w > 12) {
      y += tallest;
      x = 0;
      tallest = 0;
    }
    const type = BOARD_WIDGET_TYPE[w.kind];
    const config = boardConfig(w);
    out.push({
      id: `w-${type}-${newId()}`,
      type,
      title: w.title,
      // A widget pinned to a window of past days says which days, with the year: a headline value months old is never
      // read as live (the chip on a chart gives the window too, but a KPI and a status grid have none).
      ...(w.window ? { subtitle: windowSubtitle(w.window) } : {}),
      layout: { x, y, w: size.w, h: size.h },
      ...(w.source ? { dataSource: boardDataSource(w.source, names) } : {}),
      ...(config ? { config } : {}),
    });
    x += size.w;
    tallest = Math.max(tallest, size.h);
  }
  return out;
}

/** A new board as the page makes it from a plan: its widgets at the registry's sizes, on rows of 80 pixels. */
export function newBoardOf(id: string, name: string, widgets: WidgetInstance[], now: Date = new Date()): DashboardConfig {
  return { id, name, widgets, source: "my", owner: "You", updatedAt: now.toISOString(), cols: 12, rowHeight: 80 };
}

/** The names a plan's tag sources bind by, read from the org's tags only when one binds a tag. */
async function namesFor(plan: BoardPlan, calls: BoardCalls): Promise<Map<string, string> | { refusal: string }> {
  if (boardTagIds(plan).length === 0) return new Map();
  return boardTagNames(plan, await calls.tags());
}

/**
 * Adds a board card's widgets: below the widgets of the board it names (read as the platform holds it, then saved
 * whole under the stamp it was read at, every other widget kept, an edit saved in between never replaced: addToBoard),
 * or on a new board made with them. Nothing is written when a tag cannot be bound, or when the board kept changing
 * (WidgetRefused). Gives the board's id.
 */
export async function addBoardWidgets(plan: BoardPlan, calls: BoardCalls): Promise<{ id: string }> {
  const names = await namesFor(plan, calls);
  if ("refusal" in names) throw new WidgetRefused(names.refusal);
  if ("name" in plan.board) {
    const id = calls.newId();
    await calls.create(newBoardOf(id, plan.board.name, boardWidgetsOf(plan, names, calls.newId)));
    return { id };
  }
  // Read, add, save whole under the stamp read: a save refused as stale reads the board again and adds to that copy.
  return addToBoard(plan.board.id, calls, (board) => boardWidgetsOf(plan, names, calls.newId, nextRow(board.widgets)));
}

/** Two slot bindings that bind the same keys to the same slots, in any order. */
function sameSlots(a: Record<string, string[]> | undefined, b: Record<string, string[]> | undefined): boolean {
  const x = Object.entries(a ?? {}).filter(([, v]) => v.length > 0);
  const y = Object.entries(b ?? {}).filter(([, v]) => v.length > 0);
  return x.length === y.length && x.every(([slot, keys]) => {
    const other = b?.[slot] ?? [];
    return keys.length === other.length && keys.every((k) => other.includes(k));
  });
}

/** A widget that is the planned one: the same type, title, binding and pinned range or window. */
function sameBoardWidget(w: WidgetInstance, planned: WidgetInstance): boolean {
  if (w.type !== planned.type || (w.title ?? "").trim() !== planned.title) return false;
  const a = w.dataSource;
  const b = planned.dataSource;
  if (Boolean(a) !== Boolean(b)) return false;
  if (a && b && (a.kind !== b.kind || a.alias !== b.alias || !sameSlots(a.slots, b.slots))) return false;
  const ca = w.config ?? {};
  const cb = planned.config ?? {};
  return JSON.stringify(ca.range ?? null) === JSON.stringify(cb.range ?? null) &&
    JSON.stringify(ca.window ?? null) === JSON.stringify(cb.window ?? null);
}

/**
 * The board card's widgets where they were added already (by another tab, or by an approval whose answer was lost):
 * the id of the board that holds every one of them, or null. On a board the org has, that board; on a new board, a
 * board of the card's name.
 */
export async function findBoardWidgets(plan: BoardPlan, calls: BoardCalls): Promise<string | null> {
  const names = await namesFor(plan, calls);
  if ("refusal" in names) return null;
  const planned = boardWidgetsOf(plan, names, () => "x");
  const holds = (b: DashboardConfig) => planned.every((p) => b.widgets.some((w) => sameBoardWidget(w, p)));
  if ("id" in plan.board) {
    const board = await calls.board(plan.board.id);
    return holds(board) ? board.id : null;
  }
  const name = plan.board.name;
  return (await calls.boards()).find((b) => b.name.trim() === name && holds(b))?.id ?? null;
}

const KIND_WORDS: Record<BoardKind, [string, string]> = {
  kpi: ["KPI", "KPIs"],
  timeseries: ["time series", "time series"],
  "status-grid": ["status grid", "status grids"],
  histogram: ["histogram", "histograms"],
  table: ["table", "tables"],
  spc: ["control chart", "control charts"],
  pareto: ["chart of scrap by defect", "charts of scrap by defect"],
};

/** A board in words: its widgets of each kind, in the board's order ("A board of 6 widgets: 4 KPIs and 2 time series"). */
export function boardText(widgets: readonly { kind: BoardKind }[]): string {
  const counts = new Map<BoardKind, number>();
  for (const w of widgets) counts.set(w.kind, (counts.get(w.kind) ?? 0) + 1);
  const parts = [...counts].map(([kind, n]) => (n === 1 ? `a ${KIND_WORDS[kind][0]}` : `${n} ${KIND_WORDS[kind][1]}`));
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : (parts[0] ?? "");
  return `A board of ${widgets.length} widget${widgets.length === 1 ? "" : "s"}${list ? `: ${list}` : ""}`;
}
